<script setup lang="ts">
// The sidebar's lumi button: the Lumi glyph standing in a speech bubble — the "lu" inside,
// the rays shining out over the bubble's top edge — so the one way into the chat reads as
// "talk to lumi" rather than as a second logo.
//
// Closed, the bubble is an outline and the glyph is cut free of it (a halo in the
// sidebar's ground, `--shell-canvas`) where the two cross. Open, the bubble
// fills. Working (a turn or a run under way), only the rays pulse, one after another
// from left to right — the bubble and the letters stay put, open or closed.
//
// The rays are taken by position from LUMI_GLYPH_PATHS (see the contract in
// lumiGlyph.ts): left bar, left diagonal, vertical, right diagonal, right bar.
import { LUMI_GLYPH_DOT, LUMI_GLYPH_PATHS } from './lumiGlyph';

defineProps<{
  /** The chat is open: the bubble fills. */
  open?: boolean;
  /** Lumi is answering or a run is under way: the rays pulse. */
  working?: boolean;
}>();

const BUBBLE = 'M7 9.5H25A4 4 0 0 1 29 13.5V23.5A4 4 0 0 1 25 27.5H13L8 31V27.5H7A4 4 0 0 1 3 23.5V13.5A4 4 0 0 1 7 9.5Z';
const RAYS = [0, 5, 6, 4, 3].map((i) => LUMI_GLYPH_PATHS[i]);
const LETTERS = [1, 2].map((i) => LUMI_GLYPH_PATHS[i]);
</script>

<template>
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 -2 32 34"
    aria-hidden="true"
    class="lumi-chat-glyph"
    :class="{ 'lumi-chat-glyph--open': open, 'lumi-chat-glyph--working': working }"
  >
    <path class="lumi-chat-glyph__bubble" :d="BUBBLE" />
    <g transform="translate(2 -2.5) scale(0.875)">
      <g v-if="!open" class="lumi-chat-glyph__knockout">
        <path v-for="d in LUMI_GLYPH_PATHS" :key="d" :d="d" />
        <circle v-bind="LUMI_GLYPH_DOT" />
      </g>
      <g class="lumi-chat-glyph__mark">
        <path v-for="(d, i) in RAYS" :key="d" class="lumi-chat-glyph__ray" :style="{ animationDelay: `${i * 120}ms` }" :d="d" />
        <path v-for="d in LETTERS" :key="d" :d="d" />
        <circle v-bind="LUMI_GLYPH_DOT" />
      </g>
    </g>
  </svg>
</template>

<style>
.lumi-chat-glyph{display:block;overflow:visible;}
/* The bubble takes its colour from the button (currentColor) and draws a line as
   thick as a 24-unit outline icon's 2 at 18px: 1.5px at the 36px the sidebar
   renders it (34 units tall), so it sits beside the bell as one family. */
.lumi-chat-glyph__bubble{fill:none;stroke:currentColor;stroke-width:1.42;stroke-linejoin:round;}
.lumi-chat-glyph--open .lumi-chat-glyph__bubble{fill:currentColor;}
.lumi-chat-glyph__knockout{fill:var(--shell-canvas);stroke:var(--shell-canvas);stroke-width:5;stroke-linejoin:round;}
.lumi-chat-glyph__mark{fill:var(--attention);stroke:var(--attention);stroke-width:0.5;stroke-miterlimit:10;}
.lumi-chat-glyph--working .lumi-chat-glyph__ray{animation:lumi-chat-glyph-ray 1.5s ease-in-out infinite;}
@keyframes lumi-chat-glyph-ray{0%,40%,100%{opacity:0.2;}18%{opacity:1;}}
/* No sweep under reduced motion: the rays simply dim while Lumi works. */
@media (prefers-reduced-motion: reduce){.lumi-chat-glyph--working .lumi-chat-glyph__ray{animation:none;opacity:0.45;}}
</style>
